<template class="page-content">
  <ModalLayout :ref="modalRef" :modal-id="`assignments-modal-${matchId}`"
    :loading="isSavableStateLoading"
    @close="modalClosed">
    <template #modal-header>
      <h5 v-if="selectedMatch && selectedMatch.transplant_center" class="modal-title">
        {{ `Update Assignment - Donor: ${selectedMatch.donor.code} | Match: ${selectedMatch.code}-${selectedMatch.organ.name} | TC: ${selectedMatch.transplant_center.name}
                     | Offer Date: ${selectedMatch.offer_date}` }}
      </h5>
    </template>
    <template #modal-body>
      <form @submit.prevent="saveAssignment">
        <fieldset>
          <div class="row">
            <div class="col-sm-12">
              <div class="row">
                <MultiSelectInput v-model="selectedUser"
                  name="assignTo"
                  label="Assign To"
                  :options="users"
                  label-key="name_to_display"
                  append-to="self"
                  value-key="id"
                  col-style="form-group-5-column-large col-xl-5" />
              </div>
              <div class="save-toolbar action-row">
                <ActionToolbar :component-state="currentState" 
                  toolbar-size="col-md-12"
                  :primary-disabled="!selectedUser || isSavableStateLoading"
                  success-message="Successfully updated!"
                  primary-button-text="Update"
                  secondary-button-text="Assign to me"
                  tertiary-button-text="Unassign"
                  secondary-style="primary"
                  tertiary-style="danger"
                  :secondary-enabled="true"
                  :tertiary-enabled="true"
                  @secondary-click="assignToSelf"
                  @tertiary-click="unAssign"
                  :error-message="formError" />  
              </div>
            </div>
          </div>
        </fieldset>
      </form>
      <div class="row">
        <div class="col-sm-12 mb-2">
          <PortalTable :table-id="`assignments-table-${matchId}`"
            :table-data="assignments"
            :total-records="assignments_count"
             empty="No data found!"
            :loading="isSavableStateLoading">
            <template #headers>
              <div class="table-header">
                <span><b>Assignment History</b></span>
              </div>
            </template>
            <template #columns>
              <Column field="created_at" header="Date/time" style="min-width: 100px;">
                <template #body="slotProps">
                  {{ formatDate(slotProps.data.created_at) }}
                </template>
              </Column>
              <Column field="assigned_to" header="Assigned To" style="min-width: 100px;">
                <template #body="slotProps">
                  <span v-if="slotProps.data.assigned_to">
                    {{ slotProps.data.assigned_to }}
                  </span>
                  <span v-else><b>Unassigned</b></span>
                </template>
              </Column>
              <Column field="assigned_by" header="Assigned By" style="white-space: pre-wrap; min-width: 100px;" />
            </template>
          </PortalTable>
        </div>
      </div>
    </template>
  </ModalLayout>
</template>

<script lang="js">
import {APIRoute, EP} from "~/src/endpoints";
import beApiClient from "@/src/be-api-client";
import ModalLayout from "@/src/components/shared/ModalLayout.vue";
import ActionToolbar from "@/src/components/shared/ActionToolbar.vue";
import {savableStateMixin} from "@/src/mixins/savableStateMixin";
import {modalActionsMixin} from "@/src/mixins/modalActionsMixin";
import PortalTable from "@/src/components/shared/PortalTable.vue";
import Column from 'primevue/column';
import MultiSelectInput from "@/src/components/shared/FormComponents/MultiSelectInput.vue";
import { useUserStore } from '@/src/stores/user';
import {useAppCoreStore} from "@/src/stores/app-core";
import { useMatchStore } from '@/src/stores/match';
import { useSuperTeamDashboardStore } from '@/src/stores/dashboards/super-team-dashboards';

export default {
  components: {
    PortalTable,
    ModalLayout,
    Column,
    ActionToolbar,
    MultiSelectInput
  },
  setup() {
    const userStore = useUserStore();
    const appStore = useAppCoreStore();
    const matchStore = useMatchStore();
    const superTeamDashboardStore = useSuperTeamDashboardStore();
    return {
      userStore, appStore, matchStore, superTeamDashboardStore
    }
  },
  mixins: [savableStateMixin, modalActionsMixin],
  props: {
    matchId: { required: true, type: String },
    dashboardId: { required: true, type: String },
    users: { required: true },
  },
  emits: ['close', 'updated'],
  data() {
    return {
      selectedMatch: null,
      selectedUser: null,
      modalRef: 'assignments-modal',
      assignments: null,
      assignments_count: null
    }
  },
  watch: {
    async matchId() {
      await this.getModalData();
    }
  },
  async mounted() {
    this.selectedMatch = null; 
    this.assignments = null;
    this.assignments_count = null;
    this.setSavableStateLoading();

    try {
      await this.getModalData();
      this.setSavableStateIdle();
    } catch(error) {
      this.handleMajorError(error);
    }
  },
  methods: {
    formatDate(dateString) {
      let date = new Date(dateString);
      return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
    },
    async getModalData() {
      if(!this.matchId) return;

      try {
        this.setSavableStateLoading();
        const endpoint = APIRoute(EP.matches.show, { id: this.matchId });
        const response = await beApiClient.get(endpoint);
        this.selectedMatch = response.data; 
        if (response.data.assignment && response.data.assignment.ordered_versions) {
          this.assignments = response.data.assignment.ordered_versions;
          this.assignments_count = response.data.assignment.ordered_versions.count;
        } else {
          this.assignments = null;
          this.assignments_count = null;
        }
        this.setSavableStateIdle();
      } catch(error) {
        this.handleFormError(error);
      }
    },
    modalClosed() {
      this.setSavableStateBlank();
      this.$emit('close');
    },
    async saveAssignment() {
      this.setSavableStateProcessing();
      const payload = {
        assigned_to_id: this.selectedUser
      }
      
      try {
        await this.matchStore.offerAssignment(this.selectedMatch.id, payload);
        await this.getModalData();
        this.setSavableStateSuccess();
        this.selectedUser = null;
        this.$emit('updated');
      } catch(error) {
        this.handleFormError(error);
      }
    },
    async assignToSelf() {
      this.selectedUser = this.appStore.currentUser.profileInfo.id;
      this.saveAssignment();
    },
    unAssign() {
      this.selectedUser = null;
      this.saveAssignment();
    }
  }
}
</script>
